import type {
	GroundingEvidence,
	IdeateGroundingSnapshot,
	NCCluster,
	SocialLandscapeSegment,
} from '../../commonTypes';

/** Special Ideate analysis that visualises Foundations grounding on the board. */
export const GROUNDING_CONTEXT_ANALYSIS_KEY = 'Grounding.cluster';

export const GROUNDING_CONTEXT_ANALYSIS = {
	name: 'Grounding context',
	generate: [] as string[],
};

export const GROUNDING_CONTEXT_CLUSTER_GROUPS = [
	'Project framing',
	'Board concepts',
	'Idea Box',
	'Social landscape',
] as const;

export type GroundingContextClusterGroup = (typeof GROUNDING_CONTEXT_CLUSTER_GROUPS)[number];

export type GroundingContextClusterLayout = {
	x: number;
	y: number;
	orbitRadius: number;
	circleRadius: number;
};

const GROUNDING_CARD_SPAN = 300;
const GROUNDING_MIN_ORBIT_RADIUS = 270;
const GROUNDING_RING_PADDING = 170;
const GROUNDING_CLUSTER_GAP = 180;
/** Rich media cards are deliberately capped on-canvas; all evidence remains in the snapshot. */
const GROUNDING_SOCIAL_CARD_LIMIT = 12;

/**
 * Size the orbit from the chord between adjacent nodes. Grounding nodes are
 * card-shaped, so the legacy count * 7 radius allows their 240px surfaces to
 * overlap heavily as soon as a cluster contains more than a few items.
 */
export const getGroundingContextOrbitRadius = (valueCount: number): number => {
	if (valueCount <= 1) return GROUNDING_MIN_ORBIT_RADIUS;
	const requiredForAdjacentCards =
		GROUNDING_CARD_SPAN / (2 * Math.sin(Math.PI / valueCount));
	return Math.max(GROUNDING_MIN_ORBIT_RADIUS, Math.ceil(requiredForAdjacentCards));
};

/** Lay variable-sized grounding clusters out on a spacious two-column grid. */
export const layoutGroundingContextClusters = (
	clusters: NCCluster[]
): GroundingContextClusterLayout[] => {
	if (!clusters.length) return [];
	const layouts = clusters.map((cluster) => {
		const isSocialLandscape = cluster.group === 'Social landscape';
		// The social centre owns nested lane clusters. Keep those lane centres well
		// outside the hub, while reserving another band for the post cards orbiting
		// each lane.
		const orbitRadius = isSocialLandscape
			? Math.max(520, getGroundingContextOrbitRadius(cluster.values.length))
			: getGroundingContextOrbitRadius(cluster.values.length);
		const circleRadius = orbitRadius + GROUNDING_RING_PADDING + (isSocialLandscape ? 220 : 0);
		return { x: 0, y: 0, orbitRadius, circleRadius };
	});
	const columnCount = Math.min(2, layouts.length);
	const rowCount = Math.ceil(layouts.length / columnCount);
	const extents = layouts.map(({ circleRadius }) => circleRadius + 40);
	const columnExtents = Array.from({ length: columnCount }, (_, column) =>
		Math.max(...extents.filter((_, index) => index % columnCount === column))
	);
	const rowExtents = Array.from({ length: rowCount }, (_, row) =>
		Math.max(
			...extents.filter((_, index) => Math.floor(index / columnCount) === row)
		)
	);
	const totalWidth =
		columnExtents.reduce((sum, extent) => sum + extent * 2, 0) +
		GROUNDING_CLUSTER_GAP * (columnCount - 1);
	const totalHeight =
		rowExtents.reduce((sum, extent) => sum + extent * 2, 0) +
		GROUNDING_CLUSTER_GAP * (rowCount - 1);
	const columnCenters: number[] = [];
	const rowCenters: number[] = [];
	let cursor = -totalWidth / 2;
	for (const extent of columnExtents) {
		columnCenters.push(cursor + extent);
		cursor += extent * 2 + GROUNDING_CLUSTER_GAP;
	}
	cursor = -totalHeight / 2;
	for (const extent of rowExtents) {
		rowCenters.push(cursor + extent);
		cursor += extent * 2 + GROUNDING_CLUSTER_GAP;
	}
	return layouts.map((layout, index) => ({
		...layout,
		x: columnCenters[index % columnCount],
		y: rowCenters[Math.floor(index / columnCount)],
	}));
};

export const groundingContextNodeKey = (group: string, label: string): string =>
	`${group.trim().toLocaleLowerCase()}::${label.trim().toLocaleLowerCase()}`;

const compactLabel = (value: string, max = 72): string => {
	const trimmed = value.replace(/\s+/g, ' ').trim();
	if (!trimmed) return '';
	return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`;
};

const socialEvidenceKey = (item: GroundingEvidence): string =>
	item.socialEvidenceId || `${item.source}:${item.url || item.title}`;

export type SocialLandscapeSubcluster = {
	segment: SocialLandscapeSegment;
	profiles: GroundingEvidence[];
};

/** Build qualitative, evidence-linked lanes from the retained social sample. */
export const buildSocialLandscapeSegments = (
	profiles: GroundingEvidence[]
): SocialLandscapeSegment[] => {
	const segments: SocialLandscapeSegment[] = [];
	for (const dimension of ['stance', 'sentiment', 'emotion'] as const) {
		const groups = new Map<string, GroundingEvidence[]>();
		for (const profile of profiles) {
			const values =
				dimension === 'stance'
					? [profile.stance || 'unclear']
					: dimension === 'sentiment'
						? [profile.sentiment || 'unlabelled']
						: (profile.emotions || [])
							.map((emotion) => emotion.trim().toLocaleLowerCase())
							.filter(Boolean);
			for (const value of values) groups.set(value, [...(groups.get(value) || []), profile]);
		}
		for (const [value, items] of [...groups.entries()].sort(
			([, left], [, right]) => right.length - left.length
		)) {
			const platformCounts = items.reduce<Record<string, number>>((counts, item) => {
				const platform = item.platform || 'unknown';
				counts[platform] = (counts[platform] || 0) + 1;
				return counts;
			}, {});
			segments.push({
				id: `${dimension}:${value}`,
				label: `${dimension.charAt(0).toUpperCase()}${dimension.slice(1)} · ${value} · ${items.length} observed post${items.length === 1 ? '' : 's'}`,
				dimension,
				value,
				evidenceIds: items.map(socialEvidenceKey),
				observationCount: items.length,
				platformCounts,
			});
		}
	}
	return segments;
};

const resolveSocialLandscapeSegments = (snapshot: IdeateGroundingSnapshot): SocialLandscapeSegment[] =>
	snapshot.socialSegments?.length
		? snapshot.socialSegments
		: buildSocialLandscapeSegments(snapshot.socialProfiles || []);

export const socialLandscapeSegmentEvidence = (segment: SocialLandscapeSegment): GroundingEvidence => ({
	source: `social-segment:${segment.dimension}`,
	title: segment.label,
	detail: `Retrieved sample: ${segment.observationCount} post${segment.observationCount === 1 ? '' : 's'} across ${Object.entries(segment.platformCounts).map(([platform, count]) => `${platform} (${count})`).join(', ')}. Select to use the linked posts for regeneration; this is not a population statistic.`,
});

/**
 * Place every retained post in one interpretable lane. Segment dimensions
 * overlap, so duplicating the card under stance, sentiment and emotion would
 * inflate the landscape and make selection ambiguous. Prefer an observed
 * stance, then a meaningful sentiment, then the primary emotion.
 */
export const buildSocialLandscapeSubclusters = (
	profiles: GroundingEvidence[],
	segments: SocialLandscapeSegment[]
): SocialLandscapeSubcluster[] => {
	const segmentById = new Map(segments.map((segment) => [segment.id, segment]));
	const profilesBySegmentId = new Map<string, GroundingEvidence[]>();
	for (const profile of profiles) {
		// The board defaults to one mutually-exclusive facet: stance. Sentiment
		// and emotion remain visible on each card, instead of duplicating cards
		// across overlapping nested circles.
		const segmentId = `stance:${profile.stance || 'unclear'}`;
		if (!segmentById.has(segmentId)) continue;
		profilesBySegmentId.set(segmentId, [...(profilesBySegmentId.get(segmentId) || []), profile]);
	}
	return [...profilesBySegmentId.entries()]
		.map(([segmentId, groupedProfiles]) => ({ segment: segmentById.get(segmentId)!, profiles: groupedProfiles }))
		.sort((left, right) => right.profiles.length - left.profiles.length);
};

export const groundingEvidenceNodeLabel = (item: GroundingEvidence): string => {
	if (item.source === 'framing') {
		return item.detail && item.detail !== item.title
			? compactLabel(`${item.title}: ${item.detail}`)
			: compactLabel(item.title);
	}
	return compactLabel(item.title) || compactLabel(item.detail) || 'Grounding signal';
};

export const hasGroundingContextBoardData = (
	snapshot: IdeateGroundingSnapshot | undefined
): boolean => {
	if (!snapshot) return false;
	return Boolean(
		snapshot.framing?.length ||
			snapshot.boardConcepts?.length ||
			snapshot.likedResearch?.length ||
			snapshot.socialSegments?.length ||
			snapshot.socialProfiles?.length
	);
};

/**
 * Build L→R analysis clusters for the Grounding context board.
 * Catalog items become nodes; empty categories are omitted.
 */
export const buildGroundingContextClusters = (
	snapshot: IdeateGroundingSnapshot | undefined
): NCCluster[] => {
	if (!snapshot) return [];

	const sections: Array<{ group: GroundingContextClusterGroup; items: GroundingEvidence[] }> = [
		{ group: 'Project framing', items: snapshot.framing || [] },
		{ group: 'Board concepts', items: snapshot.boardConcepts || [] },
		{ group: 'Idea Box', items: snapshot.likedResearch || [] },
		{
			group: 'Social landscape',
			items: buildSocialLandscapeSubclusters(
				(snapshot.socialProfiles || []).slice(0, GROUNDING_SOCIAL_CARD_LIMIT),
				resolveSocialLandscapeSegments(snapshot)
			).map(({ segment }) => socialLandscapeSegmentEvidence(segment)),
		},
	];

	const clusters: NCCluster[] = [];
	for (const { group, items } of sections) {
		const seen = new Set<string>();
		const values = items
			.map(groundingEvidenceNodeLabel)
			.filter((label) => {
				const key = label.toLocaleLowerCase();
				if (!key || seen.has(key)) return false;
				seen.add(key);
				return true;
			});
		if (values.length) clusters.push({ group, values });
	}
	return clusters;
};

export const selectedGroundingContextLabels = (
	snapshot: IdeateGroundingSnapshot | undefined
): Set<string> => {
	const selected = new Set<string>();
	if (!snapshot) return selected;

	for (const item of snapshot.selectedFraming ?? snapshot.framing ?? []) {
		selected.add(groundingEvidenceNodeLabel(item));
	}
	for (const item of snapshot.selectedBoardConcepts ?? snapshot.boardConcepts ?? []) {
		selected.add(groundingEvidenceNodeLabel(item));
	}
	for (const item of snapshot.selectedLikedResearch ?? snapshot.likedResearch ?? []) {
		selected.add(groundingEvidenceNodeLabel(item));
	}
	for (const item of snapshot.selectedSocialProfiles ?? snapshot.socialProfiles ?? []) selected.add(groundingEvidenceNodeLabel(item));
	for (const segment of snapshot.socialSegments || []) {
		if ((snapshot.selectedSocialSegmentIds || []).includes(segment.id)) {
			selected.add(segment.label);
		}
	}
	return selected;
};

/** Preserve category identity so duplicate labels in different clusters toggle independently. */
export const selectedGroundingContextNodeKeys = (
	snapshot: IdeateGroundingSnapshot | undefined
): Set<string> => {
	const selected = new Set<string>();
	if (!snapshot) return selected;
	const sections: Array<{
		group: GroundingContextClusterGroup;
		items: GroundingEvidence[];
	}> = [
		{ group: 'Project framing', items: snapshot.selectedFraming ?? snapshot.framing ?? [] },
		{
			group: 'Board concepts',
			items: snapshot.selectedBoardConcepts ?? snapshot.boardConcepts ?? [],
		},
		{ group: 'Idea Box', items: snapshot.selectedLikedResearch ?? snapshot.likedResearch ?? [] },
		{
			group: 'Social landscape',
			items: (snapshot.socialSegments || [])
				.filter((segment) => (snapshot.selectedSocialSegmentIds || []).includes(segment.id))
				.map(socialLandscapeSegmentEvidence),
		},
	];
	for (const { group, items } of sections) {
		for (const item of items) {
			selected.add(groundingContextNodeKey(group, groundingEvidenceNodeLabel(item)));
		}
	}
	const socialGroupByEvidenceId = new Map(
		buildSocialLandscapeSubclusters(snapshot.socialProfiles || [], resolveSocialLandscapeSegments(snapshot)).flatMap(
			({ segment, profiles }) => profiles.map((profile) => [socialEvidenceKey(profile), segment.label] as const)
		)
	);
	for (const item of snapshot.selectedSocialProfiles ?? snapshot.socialProfiles ?? []) {
		selected.add(
			groundingContextNodeKey(
				socialGroupByEvidenceId.get(socialEvidenceKey(item)) || 'Social landscape',
				groundingEvidenceNodeLabel(item)
			)
		);
	}
	return selected;
};

export const findGroundingEvidenceByNodeLabel = (
	items: GroundingEvidence[] | undefined,
	label: string
): GroundingEvidence | undefined => {
	const needle = label.trim().toLocaleLowerCase();
	return (items || []).find((item) => groundingEvidenceNodeLabel(item).toLocaleLowerCase() === needle);
};
